/* BC Gallery — product gallery without a carousel library.
 *
 * Replaces the Flickity gallery. See snippets/bc-gallery.liquid for the why.
 *
 * The rule that matters: changing image must be O(1). No node moves, no re-measure.
 * A variant change sets `hidden` and `order` on thumbnails (property writes) and swaps
 * the stage image's src/srcset/alt. Nothing is re-parented, so nothing forces a layout
 * of the whole set. The old Flickity path blocked the main thread 264-438ms per size
 * click; this does no work proportional to slide count.
 */
(function () {
  'use strict';

  function init(root) {
    if (root.bcInit) return;
    root.bcInit = true;

    var img = root.querySelector('[data-bc-main]');
    var videoBox = root.querySelector('[data-bc-video]');
    var thumbs = [].slice.call(root.querySelectorAll('[data-bc-thumb]'));
    var status = root.querySelector('[data-bc-status]');
    var prevBtn = root.querySelector('[data-bc-prev]');
    var nextBtn = root.querySelector('[data-bc-next]');
    var zoomBtn = root.querySelector('[data-bc-zoom]');

    var media, map, startId;
    try {
      media = JSON.parse(root.querySelector('[data-bc-media]').textContent);
      map = JSON.parse(root.querySelector('[data-bc-map]').textContent);
      startId = JSON.parse(root.querySelector('[data-bc-start]').textContent);
    } catch (e) {
      return; // leave the server-rendered image in place rather than break the page
    }
    if (!img || !media || !media.length) return;

    var byId = Object.create(null);
    media.forEach(function (m) { byId[String(m.id)] = m; });

    var allIds = media.map(function (m) { return String(m.id); });
    var order = allIds.slice();
    var pos = 0;

    function clearVideo() {
      if (!videoBox) return;
      if (!videoBox.hidden) {
        videoBox.textContent = '';
        videoBox.hidden = true;
      }
      img.hidden = false;
    }

    function showVideo(m) {
      if (!videoBox) return;
      var html;
      if (m.video === 'youtube') {
        html =
          '<iframe src="https://www.youtube-nocookie.com/embed/' +
          encodeURIComponent(m.videoId) +
          '?rel=0&modestbranding=1&playsinline=1&autoplay=1" title="' +
          String(m.alt || '').replace(/"/g, '&quot;') +
          '" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowfullscreen loading="lazy"></iframe>';
      } else {
        html =
          '<video controls playsinline autoplay controlsList="nodownload" poster="' +
          m.src +
          '"><source src="' + m.videoSrc + '" type="video/mp4"></video>';
      }
      videoBox.innerHTML = html;
      videoBox.hidden = false;
      img.hidden = true;
    }

    // O(1): swap the stage image. No DOM structure changes.
    function render(i, opts) {
      opts = opts || {};
      if (!order.length) return;
      pos = ((i % order.length) + order.length) % order.length;
      var m = byId[order[pos]];
      if (!m) return;

      clearVideo();
      // Track what is rendered by media id, not by comparing src. The src attribute is the
      // 720w candidate while currentSrc may be any candidate from srcset, so an
      // attribute comparison can pass while the browser keeps resolving to the old file.
      // srcset must also be set BEFORE src: with a stale srcset still describing the
      // previous image the browser re-picks from the old candidate list and the image
      // never changes. That is why next advanced the counter but not the picture.
      if (img.dataset.mediaId !== String(m.id)) {
        img.dataset.mediaId = String(m.id);
        img.srcset = m.srcset || '';
        img.src = m.src;
        img.alt = m.alt || '';
        if (m.w) img.width = m.w;
        if (m.h) img.height = m.h;
      }

      for (var t = 0; t < thumbs.length; t++) {
        var active = thumbs[t].dataset.mediaId === String(m.id);
        if (thumbs[t].classList.contains('is-active') !== active) {
          thumbs[t].classList.toggle('is-active', active);
          if (active) thumbs[t].setAttribute('aria-current', 'true');
          else thumbs[t].removeAttribute('aria-current');
        }
        // Keep the active thumb in view WITHOUT scrollIntoView: that scrolls the nearest
        // scrollable ancestor, which includes the page, so clicking next dragged the whole
        // window down. Only the strip's own horizontal offset is touched here.
        if (active && opts.scrollThumb !== false && thumbsEl) {
          var tr = thumbs[t].getBoundingClientRect();
          var cr = thumbsEl.getBoundingClientRect();
          if (tr.left < cr.left) {
            thumbsEl.scrollLeft -= (cr.left - tr.left) + 10;
          } else if (tr.right > cr.right) {
            thumbsEl.scrollLeft += (tr.right - cr.right) + 10;
          }
        }
      }

      if (status) status.textContent = 'Image ' + (pos + 1) + ' of ' + order.length;
      if (m.video && opts.playVideo) showVideo(m);
    }

    // Variant change: filter and reorder thumbnails with property writes only.
    function applyVariant(variantId) {
      var list = map && map[String(variantId)];
      var ids = list && list.length ? list.map(String).filter(function (id) { return byId[id]; }) : null;
      order = ids && ids.length ? ids : allIds.slice();

      var rank = Object.create(null);
      order.forEach(function (id, i) { rank[id] = i; });

      for (var t = 0; t < thumbs.length; t++) {
        var id = thumbs[t].dataset.mediaId;
        var member = id in rank;
        if (thumbs[t].hidden !== !member) thumbs[t].hidden = !member;
        var ord = member ? String(rank[id]) : '';
        if (thumbs[t].style.order !== ord) thumbs[t].style.order = ord;
      }
      render(0, { scrollThumb: false });
      updateThumbNav();
    }

    // Thumbnail strip arrows: page the strip, hide at each end.
    var thumbsEl = root.querySelector('[data-bc-thumbs]');
    var tPrev = root.querySelector('[data-bc-thumbprev]');
    var tNext = root.querySelector('[data-bc-thumbnext]');
    function updateThumbNav() {
      if (!thumbsEl || !tPrev || !tNext) return;
      var max = thumbsEl.scrollWidth - thumbsEl.clientWidth;
      var atStart = thumbsEl.scrollLeft <= 1;
      var atEnd = thumbsEl.scrollLeft >= max - 1;
      var none = max <= 1;
      if (tPrev.hidden !== (atStart || none)) tPrev.hidden = atStart || none;
      if (tNext.hidden !== (atEnd || none)) tNext.hidden = atEnd || none;
    }
    function pageThumbs(dir) {
      if (!thumbsEl) return;
      thumbsEl.scrollBy({ left: dir * Math.round(thumbsEl.clientWidth * 0.8), behavior: 'smooth' });
    }
    if (tPrev) tPrev.addEventListener('click', function () { pageThumbs(-1); });
    if (tNext) tNext.addEventListener('click', function () { pageThumbs(1); });
    if (thumbsEl) thumbsEl.addEventListener('scroll', updateThumbNav, { passive: true });
    window.addEventListener('resize', updateThumbNav);

    // --- events ---
    if (prevBtn) prevBtn.addEventListener('click', function () { render(pos - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { render(pos + 1); });

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var id = btn.dataset.mediaId;
        var idx = order.indexOf(id);
        if (idx >= 0) render(idx, { playVideo: true, scrollThumb: false });
      });
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { render(pos - 1); }
      else if (e.key === 'ArrowRight') { render(pos + 1); }
    });

    // Swipe, native pointer events, no library.
    var startX = null, startY = null;
    var stage = root.querySelector('.bc-gallery__stage');
    if (stage) {
      stage.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        startX = e.clientX; startY = e.clientY;
      });
      stage.addEventListener('pointerup', function (e) {
        if (startX === null) return;
        var dx = e.clientX - startX, dy = e.clientY - startY;
        startX = startY = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) render(pos + (dx < 0 ? 1 : -1));
      });
      stage.addEventListener('pointercancel', function () { startX = startY = null; });
    }

    // Lightbox: build PhotoSwipe items from the JSON, since there are no per-slide
    // <img> elements for it to scrape any more.
    function openLightbox() {
      var el = document.querySelector('.pswp');
      if (!el || typeof window.PhotoSwipe !== 'function') return;
      var items = order.map(function (id) {
        var m = byId[id];
        if (m.video === 'youtube') {
          return { html: '<div class="pswp__video"><div class="pswp__video-frame"><iframe src="https://www.youtube-nocookie.com/embed/' + encodeURIComponent(m.videoId) + '?rel=0&modestbranding=1&playsinline=1" allow="autoplay; encrypted-media; fullscreen" allowfullscreen></iframe></div></div>' };
        }
        if (m.video === 'mp4') {
          return { html: '<div class="pswp__video"><div class="pswp__video-frame"><video controls playsinline poster="' + m.src + '"><source src="' + m.videoSrc + '" type="video/mp4"></video></div></div>' };
        }
        return { src: m.zoom, msrc: m.src, w: m.w, h: m.h };
      });
      var gallery = new window.PhotoSwipe(el, window.PhotoSwipeUI_Default, items, {
        index: pos,
        history: false,
        shareEl: false,
        counterEl: false,
        captionEl: false,
        preloaderEl: false,
        bgOpacity: 1,
        getThumbBoundsFn: function () {
          var r = img.getBoundingClientRect();
          return { x: r.left, y: r.top + (window.pageYOffset || 0), w: r.width };
        }
      });
      gallery.init();
      gallery.listen('afterChange', function () { render(gallery.getCurrentIndex(), { scrollThumb: false }); });
    }
    if (zoomBtn) zoomBtn.addEventListener('click', openLightbox);
    if (img) img.addEventListener('click', openLightbox);

    // The theme dispatches variantChange on the product container.
    var scope = root.closest('[data-section-id]') || document;
    scope.addEventListener('variantChange', function (e) {
      var v = e && e.detail && e.detail.variant;
      if (v && v.id) applyVariant(v.id);
    });
    document.addEventListener('variant:change', function (e) {
      var v = e && e.detail && (e.detail.variant || e.detail);
      if (v && v.id) applyVariant(v.id);
    });

    // Initial state: honour the server-rendered starting media so nothing swaps on load.
    var startIdx = allIds.indexOf(String(startId));
    var form = document.querySelector('[name="id"]');
    if (form && map && map[String(form.value)]) {
      applyVariant(form.value);
    } else {
      render(startIdx >= 0 ? startIdx : 0, { scrollThumb: false });
    }
    updateThumbNav();
  }

  function boot() {
    document.querySelectorAll('[data-bc-gallery]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
